import { useMemo, useState } from 'react';
import { CalendarPlus, Inbox } from 'lucide-react';
import { ApplyLeaveModal, type LeaveSubject } from '@/components/leave/ApplyLeaveModal';
import { LeaveRequestsCard } from '@/components/leave/LeaveRequestsCard';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useRefreshKey } from '@/hooks/useRefreshKey';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import type { StudentLeaveRequest } from '@/types/leave';
import { studentLeaveApi } from '../api/leave.api';

export interface StudentLeaveRequestsProps {
  /** Who is asking, for the card at the top of the form. Null while it loads. */
  child: LeaveSubject | null;
}

/**
 * The child asking their class teacher to be excused, and seeing the answer.
 *
 * The parent panel's leave tab from the other side: the same list and the same
 * form (`components/leave/`), over the same rows. The list includes what their
 * family has filed, so a child does not ask again for days already asked about.
 */
export function StudentLeaveRequests({ child }: StudentLeaveRequestsProps) {
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');

  /**
   * Changes on every arrival at this tab and whenever the bell finds a new
   * notification pointing here, so a decision shows without a manual refresh.
   * As on the teacher's leave page.
   */
  const refreshKey = useRefreshKey();

  const { data: requests, isLoading, error, refetch } = useApiQuery(
    () => studentLeaveApi.list(),
    [refreshKey],
  );

  const filteredRequests = useMemo(() => {
    if (!requests) return null;
    if (!selectedDate) return requests;
    return requests.filter((r) => {
      const covers = r.fromDate <= selectedDate && selectedDate <= r.toDate;
      const requestedOn = r.requestedOn?.startsWith(selectedDate);
      return covers || requestedOn;
    });
  }, [requests, selectedDate]);

  /** Taking back a request nobody has answered yet. */
  async function withdraw(request: StudentLeaveRequest) {
    setWithdrawing(request.uuid);

    try {
      await studentLeaveApi.cancel(request.uuid);
    } catch (caught) {
      // The server names the reason — most often that the class teacher
      // answered it while this page was open.
      toast.error(
        'Could not withdraw that request',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setWithdrawing(null);
      refetch();
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-2">
          Ask your class teacher to excuse you from school. Their answer appears here.
        </p>
        <div className="flex items-center gap-2.5 flex-wrap">
          <DatePicker
            label="Filter by date"
            hideLabel
            size="sm"
            isClearable
            placeholder="Filter by date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-40 sm:w-44 !h-9 !rounded-xl text-xs sm:text-sm font-semibold bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"
          />
          <Button
            size="sm"
            onClick={() => setIsApplyOpen(true)}
            disabled={!child}
            leftIcon={<CalendarPlus className="h-4 w-4" />}
          >
            Apply for leave
          </Button>
        </div>
      </div>

      {requests && requests.length > 0 && filteredRequests?.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 text-center flex flex-col items-center justify-center shadow-xs">
          <div className="h-12 w-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-3 shadow-2xs">
            <Inbox className="h-6 w-6" />
          </div>
          <h4 className="text-base font-bold text-slate-900 dark:text-white">
            No leave requests found for this date
          </h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium max-w-md">
            No leave requests cover {formatDate(selectedDate)}. Try choosing a different date or clear the filter.
          </p>
          <button
            type="button"
            onClick={() => setSelectedDate('')}
            className="mt-3 px-3.5 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-xl cursor-pointer transition-colors"
          >
            Clear filter
          </button>
        </div>
      ) : (
        <LeaveRequestsCard
          requests={filteredRequests}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          onApply={child ? () => setIsApplyOpen(true) : undefined}
          onWithdraw={(request) => void withdraw(request)}
          withdrawing={withdrawing}
          forSelf
        />
      )}

      <ApplyLeaveModal
        isOpen={isApplyOpen}
        onClose={() => setIsApplyOpen(false)}
        child={child}
        onSubmit={studentLeaveApi.request}
        onSubmitted={refetch}
        forSelf
      />
    </div>
  );
}